import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import type { IntelligenceTopicRecord } from "@graftus/schema";
import type { PulseCompletionWithFrequency } from "@/hooks/use-pulse-check-in-nudge";
import type { CurrentTopicKey, TopicKey } from "./topics";
import { cn } from "@/lib/utils";
import { PulseCheckInChatNudge } from "./PulseCheckInChatNudge";
import { PulseSetupChatNudge } from "./PulseSetupChatNudge";
import { TOPIC_ICONS } from "./topic-icons";
import { lilacHeadline } from "@/lib/lilac-classes";

interface Props {
  contextLabel: string;
  topicKey: TopicKey;
  onTopicChange: (key: CurrentTopicKey) => void;
  topics: IntelligenceTopicRecord[];
  /** Fills the composer; user sends explicitly (Enter / Skicka). */
  onPrefillPrompt: (prompt: string) => void;
  pulseCheckInSummary?: PulseCompletionWithFrequency | null;
  showPulseSetupNudge?: boolean;
  /** Drop the icon + heading block; the landing greeting already says it. */
  hideHeading?: boolean;
}

export function ChatStarters({
  contextLabel,
  topicKey,
  onTopicChange,
  topics,
  onPrefillPrompt,
  pulseCheckInSummary,
  showPulseSetupNudge = false,
  hideHeading = false,
}: Props) {
  const { t } = useTranslation("intelligence");

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-2xl flex-col items-center justify-center gap-6 px-4 text-center",
        hideHeading ? "py-0" : "h-full py-10",
      )}
    >
      {showPulseSetupNudge ? (
        <PulseSetupChatNudge />
      ) : pulseCheckInSummary ? (
        <PulseCheckInChatNudge
          summary={pulseCheckInSummary}
          onPrefillPrompt={onPrefillPrompt}
        />
      ) : null}
      {!hideHeading && (
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-g-primary-tint text-g-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <h2 className={lilacHeadline}>
            {t("chat.starters.heading", { context: contextLabel })}
          </h2>
          <p className="max-w-md text-sm text-g-ink-500">
            {t("chat.composer.placeholder")}
          </p>
        </div>
      )}

      <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
        {topics.map((t) => {
          const Icon = TOPIC_ICONS[t.key] ?? Sparkles;
          const active = t.key === topicKey;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                onTopicChange(t.key);
                onPrefillPrompt(t.starter);
              }}
              className={cn(
                "group flex items-start gap-3 rounded-g-xl border border-g-line bg-g-bg-card p-3 text-left transition-colors hover:border-g-primary-muted hover:bg-g-primary-wash",
                active && "border-g-primary-muted bg-g-primary-wash",
              )}
            >
              <div
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-g-lg bg-g-bg-track text-g-ink-500 transition-colors",
                  "group-hover:bg-g-primary-tint group-hover:text-g-primary",
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium">{t.title}</div>
                <p className="mt-0.5 line-clamp-2 text-xs text-g-ink-500">
                  {t.starter}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
